/**
 * "Somebody is already doing this — do I join them, or queue behind them?"
 *
 * A module-global `Promise | null` guarding a non-idempotent call is the most
 * repeated shape in the client's session layer, and the repeated part is not
 * the promise: it is deciding *when to clear the slot*. That was written four
 * different ways across eight sites — `??=` with an unconditional clear, an
 * imperative clear in a `finally`, a conditional identity check, and a clear
 * inside a `then`. Every one of them was correct for its own site, which is
 * the problem: the reader has to re-derive which discipline this site needs,
 * and the identity check is the one that fails silently when it was the one
 * required.
 *
 * So the slot bookkeeping lives here once, and the call site picks a verb:
 *
 * - **`join`** coalesces. While a run is in the air, every caller gets *that*
 *   run. For work where a second answer would be the same answer, or worse:
 *   restoring a session, enrolling a device, signing out.
 * - **`after`** serializes. Every caller runs, but not until the one before it
 *   has settled. For work that creates or cancels something on the server,
 *   where discarding a response cannot undo the row it made.
 *
 * `.claude/rules/ts/async_lifecycles.md` § "Choose ordering from the side
 * effects" is the rule this implements: latest-request-wins suits replaceable
 * reads, and writes that share server state need ordering decided before the
 * request rather than after the reply.
 *
 * **One verb per slot.** `join` hands back whatever the slot holds, and `after`
 * claims the slot before its operation starts, so a flight that mixed them could
 * hand a `join` caller a different operation's result. Every slot here uses one
 * verb throughout: `after` for `signingIn`, the queue and each private table's
 * leave slot, `join` for the rest.
 *
 * Not everything belongs here. `offlineSync`'s follow-up slot deliberately
 * hands a late caller a *different* run rather than the one in the air, because
 * the request already out covers only the moves that left with it. That is a
 * third idea with its own reasoning, and it stays written out where it happens.
 */
export interface Flight<T> {
  /** Joins the run in the air if there is one; otherwise starts this one. */
  readonly join: (operation: () => Promise<T>) => Promise<T>;
  /** Waits for the run in the air to settle, then runs this one regardless. */
  readonly after: (operation: () => Promise<T>) => Promise<T>;
  /** The run in the air, or null — for a caller that must also wait on another. */
  readonly current: () => Promise<T> | null;
  /** Settles when nothing is in the air. */
  readonly idle: () => Promise<void>;
}

export function flight<T = void>(): Flight<T> {
  let inAir: Promise<T> | null = null;

  /*
   * Cleared by identity, never unconditionally. `after` lets a successor claim
   * the slot while this run is still settling, and an unconditional clear would
   * then retire the successor instead of this run — the slot goes empty, and the
   * next caller starts concurrently with a run that is still out. `join` cannot
   * produce a successor, so identity is simply the stricter of the two and is
   * correct for both.
   */
  const claim = (start: () => Promise<T>): Promise<T> => {
    const mine: Promise<T> = start().finally(() => {
      if (inAir === mine) inAir = null;
    });
    inAir = mine;
    return mine;
  };

  return {
    join: (operation) => inAir ?? claim(operation),
    after: (operation) => {
      const previous = inAir;
      return claim(async () => {
        // A failure ahead of us is that caller's to report, not a reason to
        // skip our turn: we queued for ordering, not for their result.
        if (previous !== null) await previous.catch(() => undefined);
        return operation();
      });
    },
    current: () => inAir,
    idle: async () => {
      // A loop rather than a single await: `after` can hand the slot straight
      // to a successor, and "nothing in the air" has to mean all of them.
      while (inAir !== null) await inAir.catch(() => undefined);
    },
  };
}

/**
 * A one-time bootstrap that memoizes its result for the life of the process.
 *
 * Distinct from `join`: the slot is never cleared, so a caller arriving an hour
 * later still gets the original run rather than starting a second one.
 *
 * **It memoizes a rejection too**, and there is no retry. That is correct for
 * the one thing using it — `initPlatform`, whose `bootstrap` is documented
 * never to reject — and it is a trap for anything else. If you reach for this
 * for work that can fail, you are asking for one failure to be permanent; use
 * `join` instead, which clears its slot on rejection and lets the next caller
 * try again.
 */
export function once<T>(operation: () => Promise<T>): () => Promise<T> {
  let result: Promise<T> | null = null;
  return () => (result ??= operation());
}
